
import { createClient } from '@supabase/supabase-js'
import { Database } from './types/database.types'

const SUPABASE_URL_FALLBACK = 'https://bikusyozqktomenrosbg.supabase.co'
const SUPABASE_ANON_KEY_FALLBACK = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJpa3VzeW96cWt0b21lbnJvc2JnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzI4Njk4NjIsImV4cCI6MjA0ODQ0NTg2Mn0.Yeq4xiKK4ivUTJonAoorhHYYktGXAwpDwHAOWjzw5Zs'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || SUPABASE_URL_FALLBACK
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || SUPABASE_ANON_KEY_FALLBACK

// Simple function to get project ref from URL
const getProjectRef = () => {
  try {
    const url = new URL(supabaseUrl);
    return url.hostname.split('.')[0];
  } catch (e) {
    console.error('Error extracting project ref:', e);
    return 'bikusyozqktomenrosbg'; // Fallback to hardcoded project ID
  }
};

const projectRef = getProjectRef();
// Use the new Supabase storage key format
const storageKey = `sb-${projectRef}-auth-token`;

// Debug storage to help with troubleshooting
const debugStorageKeys = () => {
  try {
    if (typeof localStorage !== 'undefined') {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('sb-'))) {
          keys.push({
            key,
            hasValue: !!localStorage.getItem(key)
          });
        }
      }
      console.log('Supabase-related localStorage keys:', keys);
    }
  } catch (e) {
    console.error('Error debugging localStorage:', e);
  }
};

// Debug storage keys on initialization
debugStorageKeys();

// Add console message to confirm client creation
console.log('Creating Supabase client with URL:', supabaseUrl ? 'Defined' : 'Undefined');
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables!');
  console.log('Environment variables available:', Object.keys(import.meta.env).filter(key => key.startsWith('VITE_')));
}

// Initialize the Supabase client with improved persistence settings
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: storageKey,
    detectSessionInUrl: true,
    flowType: 'pkce' // Use PKCE flow for better security
  },
  global: {
    fetch: (...args) => {
      // Improved fetch with retry logic for network errors
      return fetchWithRetry(args[0] as RequestInfo, args[1] as RequestInit);
    }
  }
});

// Helper function to implement fetch with retry logic (network errors + 503/504 for idempotent requests)
async function fetchWithRetry(
  input: RequestInfo,
  init?: RequestInit,
  retries = 3,
  backoff = 300
): Promise<Response> {
  const method = (init?.method || 'GET').toUpperCase();
  try {
    const resp = await fetch(input, init);
    // Retry on transient upstream errors for idempotent requests
    if ((resp.status === 503 || resp.status === 504) && retries > 0 && (method === 'GET' || method === 'HEAD')) {
      await new Promise((r) => setTimeout(r, backoff));
      console.warn(`Retrying ${method} after ${resp.status}, attempts left: ${retries - 1}`);
      return fetchWithRetry(input, init, retries - 1, backoff * 2);
    }
    return resp;
  } catch (error) {
    // Only retry on network errors
    if (retries <= 1) throw error;
    await new Promise((resolve) => setTimeout(resolve, backoff));
    console.warn(`Network error, retrying ${method}. Attempts left: ${retries - 1}`);
    return fetchWithRetry(input, init, retries - 1, backoff * 2);
  }
}

// Clear any old storage formats to prevent conflicts
const cleanupOldStorageFormats = () => {
  try {
    // Find and remove old token formats
    const oldKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Only clean up keys that aren't our current format
      if (key && key.includes('supabase.auth.token') && key !== storageKey) {
        oldKeys.push(key);
      }
    }
    
    // Remove old keys
    oldKeys.forEach(key => {
      console.log('Removing old auth key format:', key);
      localStorage.removeItem(key);
    });
  } catch (e) {
    console.error('Error cleaning up old storage keys:', e);
  }
};

// Clean up on initialization
cleanupOldStorageFormats();

// Add a function to explicitly force redirect to another page
// This provides a direct method to redirect users when needed
export const forceRedirect = (path: string) => {
  try {
    console.log(`Force redirecting to: ${path}`);
    // Use window.location for most reliable redirect
    window.location.href = path;
  } catch (e) {
    console.error('Error during force redirect:', e);
  }
};

// Export a function to check auth status
export const checkAuthStatus = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error checking auth status:', error);
      return { isAuthenticated: false, session: null, error };
    }
    
    // Log session information for debugging
    if (data.session) {
      console.log('Active session found:', {
        userId: data.session.user.id,
        expiresAt: new Date(data.session.expires_at! * 1000).toISOString()
      });
    } else {
      console.log('No active session found');
    }
    
    return { 
      isAuthenticated: !!data.session,
      session: data.session,
      error: null
    };
  } catch (e) {
    console.error('Exception checking auth status:', e);
    return { isAuthenticated: false, session: null, error: e };
  }
};

// Log the current auth status when the client is initialized
checkAuthStatus().then(status => {
  console.log('Initial auth status:', status.isAuthenticated ? 'Authenticated' : 'Not authenticated');
});

// Add a listener for auth state changes to help with debugging
supabase.auth.onAuthStateChange((event, session) => {
  console.log('Auth state changed:', event, session ? 'Session exists' : 'No session');
  debugStorageKeys();
  
  // For certain auth events, handle redirect immediately
  if (event === 'SIGNED_IN' && window.location.pathname === '/auth') {
    console.log('Signed in detected on auth page, handling redirect...');
    
    // Get return path or default to settings
    const urlParams = new URLSearchParams(window.location.search);
    let returnTo = urlParams.get('returnTo') || '/settings';
    
    // If returnTo is not absolute, ensure it has a leading slash
    if (!returnTo.startsWith('http') && !returnTo.startsWith('/')) {
      returnTo = `/${returnTo}`;
    }
    
    // Use setTimeout to ensure redirect happens after auth state is fully processed
    setTimeout(() => {
      console.log(`Redirecting to ${returnTo} after sign in`);
      window.location.href = returnTo;
    }, 500);
  }
  
  // Force refetch the profile data after certain auth events
  if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
    console.log('Auth event may require profile refresh:', event);
    // Clear any cached requests to force fresh data
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('supabase-auth-update'));
    }
  }
});
